import type { ApiClient } from './ApiClient';

// SCH-06 — formes alignées sur POST/GET /students et POST /students/import/*
// (apps/api/src/routes/students.ts), seule source de vérité pour ces routes.
export type Sex = 'M' | 'F';

export interface Student {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: Sex;
  matricule: string;
  // SCH-12 : contact parental en texte libre — voir le commentaire sur le modèle
  // Student côté backend (apps/api/prisma/schema.prisma) pour le détail du
  // compromis (pas de FK vers un profil parent réel avant PAR-01/02).
  parentContactName: string | null;
  // SCH-21 — métadonnées de contact parental issues de l'import CSV en masse.
  // Purement informatives : AUCUN compte `User`/`Parent` derrière, aucune FK.
  // `null` pour tout élève créé autrement, ou importé sans ces colonnes.
  parentContactFirstName: string | null;
  parentContactLastName: string | null;
  parentContactEmail: string | null;
  parentContactPhone: string | null;
  // ENR-30 (PR 3/3) — rôle du contact parental, ensemble FERMÉ
  // (mother/father/guardian, GuardianRelationship/PAR-04), validé
  // applicativement côté serveur — voir apps/api/src/routes/students.ts.
  parentContactRelationship: string | null;
  // SCH-14 : informations complémentaires, toutes optionnelles sans exception —
  // voir apps/api/prisma/schema.prisma (modèle Student) pour le détail complet.
  birthPlace: string | null;
  nationality: string | null;
  addressRegion: string | null;
  addressDepartment: string | null;
  addressCommune: string | null;
  enrollmentDate: string | null;
  currentClassId: string | null;
  currentClassName: string | null;
  // Maquette "Eleves.dc.html" — voir apps/api/src/lib/studentPaymentStatus.ts pour
  // le calcul exact. `null` = pas d'inscription courante ou aucune facture TUITION
  // pertinente pour l'instant (jamais un faux "à jour").
  paymentStatus: 'PAID' | 'PARTIAL' | 'LATE' | null;
  createdAt: string;
}

export interface ExistingStudentMatch {
  existingStudentId: string;
  firstName: string;
  lastName: string;
  currentClassId: string | null;
  currentClassName: string | null;
}

export type DuplicateResolution = { decision: 'different' } | { decision: 'same'; existingStudentId: string };

export interface CapacityWarning {
  className: string;
  capacity: number;
  currentCount: number;
}

// `ApiClient.post()` lève une `ApiError` sur tout statut non-2xx (voir
// packages/api-client/src/ApiClient.ts) — un 409 `duplicate_student`/`class_
// capacity_exceeded` sont pourtant des résultats ATTENDUS de ce flux, pas des
// erreurs de transport. Cette fonction attrape SPÉCIFIQUEMENT ces deux cas (via
// `error.body`, déjà exposé par `ApiError`) et les retourne comme une valeur
// normale ; toute AUTRE erreur (400, 401, 500...) continue de se propager telle
// quelle, inchangée.
export async function postAllowingDuplicate<T>(client: ApiClient, path: string, body: unknown, accessToken: string): Promise<T> {
  try {
    return await client.post<T>(path, body, { accessToken });
  } catch (err) {
    const apiError = err as { status?: number; body?: unknown };
    if (apiError.status === 409 && typeof apiError.body === 'object' && apiError.body !== null) {
      if ('duplicate' in apiError.body || 'capacity' in apiError.body) {
        return apiError.body as T;
      }
    }
    throw err;
  }
}

export interface ListStudentsResponse {
  students: Student[];
}

export function listStudents(client: ApiClient, accessToken: string, classId?: string): Promise<ListStudentsResponse> {
  return client.get<ListStudentsResponse>('/students', { accessToken, query: { classId } });
}

export interface ImportRowSummary {
  line: number;
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: string;
}

export interface ImportDuplicateRow extends ImportRowSummary {
  existingStudent: { id: string; firstName: string; lastName: string; currentClassName: string | null };
}

export interface ImportRowError {
  line: number;
  reason: string;
}

export interface ImportClassBucket {
  valid: ImportRowSummary[];
  duplicates: ImportDuplicateRow[];
  errors: ImportRowError[];
}

export interface ImportPreviewResponse {
  byClass: Record<string, ImportClassBucket>;
  totalLines: number;
  summary: { valid: number; duplicates: number; errors: number };
}

export function previewStudentImport(client: ApiClient, csv: string, accessToken: string): Promise<ImportPreviewResponse> {
  return client.post<ImportPreviewResponse>('/students/import/preview', { csv }, { accessToken });
}

// SCH-18 : une ligne "élève différent" confirmée crée désormais une VRAIE
// demande d'inscription PENDING/WAITLISTED (jamais un élève actif directement) —
// voir apps/api/src/routes/students.ts#POST /students/import/confirm.
// `enrollmentStatus`/`requestedLevel`/`schoolYear` UNIQUEMENT présents pour
// `kind === 'created'`. Une ligne "transferred"/"noop_same_class" (transfert
// d'un élève DÉJÀ existant) reste INCHANGÉE, hors périmètre de ce ticket.
export interface ImportConfirmResponse {
  results: {
    line: number;
    kind: 'created' | 'transferred' | 'noop_same_class';
    studentId: string;
    enrollmentStatus?: 'PENDING' | 'WAITLISTED';
    requestedLevel?: string;
    schoolYear?: string;
  }[];
  summary: { created: number; transferred: number; noop: number; waitlisted: number };
}

// ENR-27 : `discount` OPTIONNEL, appliqué à CHAQUE nouvel élève créé par CET
// appel confirm (jamais aux transferts) — voir apps/api/src/routes/students.ts.
export interface ConfirmStudentImportDiscount {
  discountTypeId: string;
  discountReason: string;
}

export function confirmStudentImport(
  client: ApiClient,
  csv: string,
  resolutions: Record<number, DuplicateResolution>,
  accessToken: string,
  discount?: ConfirmStudentImportDiscount,
): Promise<ImportConfirmResponse> {
  return client.post<ImportConfirmResponse>(
    '/students/import/confirm',
    { csv, resolutions, ...(discount ?? {}) },
    { accessToken },
  );
}

export interface ImportTemplateResponse {
  csv: string;
  filename: string;
}

// Gabarit CSV renvoyé en JSON (pas `text/csv`, voir routes/students.ts) — le
// frontend construit lui-même un Blob à partir de `csv` pour déclencher le
// téléchargement (voir apps/web-admin/src/pages/Students.tsx).
export function getStudentImportTemplate(client: ApiClient, accessToken: string): Promise<ImportTemplateResponse> {
  return client.get<ImportTemplateResponse>('/students/import/template', { accessToken });
}

export interface SearchStudentsResponse {
  students: Student[];
}

// SCH-12 : `q` couvre à la fois une recherche par nom (sous-chaîne, prénom OU nom
// de famille) et par matricule (égalité exacte) — un seul champ de recherche côté
// UI, voir apps/api/src/routes/students.ts#GET /students/search pour le détail.
export function searchStudents(client: ApiClient, q: string, accessToken: string): Promise<SearchStudentsResponse> {
  return client.get<SearchStudentsResponse>('/students/search', { accessToken, query: { q } });
}

export interface GetStudentResponse {
  student: Student;
}

export function getStudent(client: ApiClient, studentId: string, accessToken: string): Promise<GetStudentResponse> {
  return client.get<GetStudentResponse>(`/students/${studentId}`, { accessToken });
}

// ⚠️ `matricule` volontairement ABSENT de ce type — POST /students/:id le rejette
// explicitement (400 `matricule_not_editable`) s'il est présent dans le corps, voir
// apps/api/src/routes/students.ts. Tous les champs optionnels : seuls ceux fournis
// sont modifiés (PATCH partiel, pas un remplacement complet de la fiche).
export interface UpdateStudentPayload {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  sex?: Sex;
  parentContactName?: string | null;
  // ENR-32 — contrairement aux autres champs de contact, `null` REFUSÉ par le
  // backend (400 `parent_contact_phone_required`) : jamais effaçable une fois
  // renseigné. Type délibérément `string` (jamais `| null`) pour empêcher
  // l'appelant de compiler un payload qui l'efface — omettre la clé pour
  // laisser la valeur existante inchangée.
  parentContactPhone?: string;
  // ENR-29 — prénom/nom/email du contact parental, TOUS optionnels
  // indépendamment (voir apps/api/src/routes/students.ts) : `parentContactName`
  // reste DÉRIVÉ côté serveur du couple prénom/nom dès que les deux sont
  // connus (invariant SCH-21), jamais recomposé ici.
  parentContactFirstName?: string | null;
  parentContactLastName?: string | null;
  parentContactEmail?: string | null;
  // ENR-30 (PR 3/3) — même patron que les 3 champs ci-dessus : optionnel
  // indépendamment, `null` efface. Validé côté serveur via
  // isValidGuardianRelationship() (PAR-04).
  parentContactRelationship?: string | null;
  // SCH-14 — région/département/commune doivent être fournis ENSEMBLE (les 3)
  // ou omis ensemble, jamais partiellement (voir la route PATCH /students/:id
  // côté backend, qui rejette explicitement une combinaison incohérente).
  birthPlace?: string | null;
  nationality?: string | null;
  addressRegion?: string | null;
  addressDepartment?: string | null;
  addressCommune?: string | null;
  enrollmentDate?: string | null;
}

export interface UpdateStudentResponse {
  student: Student;
}

export function updateStudent(
  client: ApiClient,
  studentId: string,
  payload: UpdateStudentPayload,
  accessToken: string,
): Promise<UpdateStudentResponse> {
  return client.patch<UpdateStudentResponse>(`/students/${studentId}`, payload, { accessToken });
}

// PAY-17 — formes alignées sur GET /students/missing-invoices et POST
// /students/:id/missing-invoices (apps/api/src/routes/students.ts), seule
// source de vérité.
export type MissingInvoiceFeeType = 'REGISTRATION' | 'TUITION';

export interface MissingInvoiceEntry {
  studentId: string;
  firstName: string;
  lastName: string;
  enrollmentId: string;
  requestedLevel: string;
  schoolYear: string;
  missingRegistration: boolean;
  missingTuition: boolean;
}

export interface ListMissingInvoicesResponse {
  students: MissingInvoiceEntry[];
}

export function listMissingInvoices(client: ApiClient, accessToken: string): Promise<ListMissingInvoicesResponse> {
  return client.get<ListMissingInvoicesResponse>('/students/missing-invoices', { accessToken });
}

// `POST /students/:id/missing-invoices` répond `409 invoice_already_exists`/
// `404 student_not_eligible` comme des résultats métier ATTENDUS (l'admin peut
// cliquer sur une liste légèrement périmée) — même discipline que
// `postAllowingDuplicate()` ci-dessus, PAS une `ApiError` générique à faire
// remonter au formulaire.
export type CreateMissingInvoiceResult =
  | { kind: 'accepted'; enrollmentId: string; feeType: MissingInvoiceFeeType }
  | { kind: 'already_exists' }
  | { kind: 'not_eligible' };

export async function createMissingInvoice(
  client: ApiClient,
  studentId: string,
  feeType: MissingInvoiceFeeType,
  accessToken: string,
): Promise<CreateMissingInvoiceResult> {
  try {
    const result = await client.post<{ enrollmentId: string; feeType: MissingInvoiceFeeType }>(
      `/students/${studentId}/missing-invoices`,
      { feeType },
      { accessToken },
    );
    return { kind: 'accepted', enrollmentId: result.enrollmentId, feeType: result.feeType };
  } catch (err) {
    const apiError = err as { status?: number };
    if (apiError.status === 409) return { kind: 'already_exists' };
    if (apiError.status === 404) return { kind: 'not_eligible' };
    throw err;
  }
}

// UX-44 — forme alignée sur GET /students/sex-breakdown
// (apps/api/src/routes/students.ts), seule source de vérité. Décompte SIMPLE
// (élèves APPROVED de l'année scolaire ACTIVE), pas de troisième valeur —
// `Student.sex` est un enum à 2 valeurs NOT NULL (SCH-06), aucun cas "inconnu"
// à représenter.
export interface StudentSexBreakdown {
  male: number;
  female: number;
}

export function getStudentSexBreakdown(client: ApiClient, accessToken: string): Promise<StudentSexBreakdown> {
  return client.get<StudentSexBreakdown>('/students/sex-breakdown', { accessToken });
}
